const baseUrl = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

/** Fallback when the admin timeout setting is missing or unreachable. */
export const DEFAULT_KIOSK_TIMEOUT_MS = 45_000;
/** Fallback QR payment expiry when admin setting is missing. */
export const DEFAULT_QR_EXPIRY_MS = 120_000;

let cachedTimeoutMs = null;
let cachedQrExpiryMs = null;
let inflightSettings = null;

const parseMilliseconds = (value) => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.round(value);
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = parseInt(value, 10);
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;
  }
  return null;
};

const pickField = (payload, field) => {
  if (!payload || typeof payload !== 'object') return null;
  return (
    parseMilliseconds(payload[field]) ||
    parseMilliseconds(payload.data?.[field]) ||
    parseMilliseconds(payload.data?.data?.[field])
  );
};

const extractMilliseconds = (payload) => pickField(payload, 'milliseconds');

const fetchTimeoutPayload = async () => {
  const response = await fetch(`${baseUrl}/kiosk-timeout-setting`, {
    headers: { accept: '*/*' },
  });
  if (!response.ok) throw new Error(`kiosk-timeout-setting ${response.status}`);
  return response.json();
};

const loadTimeoutSettings = async () => {
  try {
    const payload = await fetchTimeoutPayload();
    return {
      milliseconds: extractMilliseconds(payload) || DEFAULT_KIOSK_TIMEOUT_MS,
      qrExpiryMs: pickField(payload, 'qr_expiry') || DEFAULT_QR_EXPIRY_MS,
    };
  } catch {
    return {
      milliseconds: DEFAULT_KIOSK_TIMEOUT_MS,
      qrExpiryMs: DEFAULT_QR_EXPIRY_MS,
    };
  }
};

const loadSettingsOnce = async () => {
  if (!inflightSettings) {
    inflightSettings = loadTimeoutSettings().finally(() => {
      inflightSettings = null;
    });
  }
  const settings = await inflightSettings;
  cachedTimeoutMs = settings.milliseconds;
  cachedQrExpiryMs = settings.qrExpiryMs;
  return settings;
};

/** Idle timeout (ms) from the admin panel, cached for the page lifetime. */
export const fetchKioskTimeoutMs = async () => {
  if (cachedTimeoutMs) return cachedTimeoutMs;
  const settings = await loadSettingsOnce();
  return settings.milliseconds;
};

/** QR code expiry (ms) from the admin panel (`qr_expiry`). */
export const fetchQrExpiryMs = async () => {
  if (cachedQrExpiryMs) return cachedQrExpiryMs;
  const settings = await loadSettingsOnce();
  return settings.qrExpiryMs;
};
